import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import minisiteService from '../services/minisiteService';
import BlockRenderer from '../components/Minisite/BlockRenderer';
import './PublicMinisite.css';

import MailIcon from '../assets/icons/minisite-mail.svg';
import PhoneIcon from '../assets/icons/minisite-phone.svg';
import PinIcon from '../assets/icons/minisite-pin.svg';
import { useI18n } from '../i18n';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8648/api').replace('/api', '');

const getStorageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/storage/')) return `${BACKEND_URL}${path}`;
    return `${BACKEND_URL}/storage/${path}`;
};

const NotAvailablePage = ({ t }) => (
    <div className="pm-unavailable">
        <div className="pm-unavailable-card">
            <div className="pm-unavailable-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path d="M20 4C14.477 4 10 8.477 10 14v2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2V18a2 2 0 0 0-2-2h-2v-2c0-5.523-4.477-10-10-10zm0 3a7 7 0 0 1 7 7v2H13v-2a7 7 0 0 1 7-7zm0 14a3 3 0 0 1 1 5.83V30a1 1 0 0 1-2 0v-3.17A3 3 0 0 1 20 21z" fill="#3b82f6" opacity=".9" />
                </svg>
            </div>
            <h1>{t('messages.public_minisite.coming_soon')}</h1>
            <p>{t('messages.public_minisite.coming_soon_description')}</p>
            <div className="pm-unavailable-badge">
                {t('messages.public_minisite.powered_by')} <strong>&nbsp;{t('common.app_name')}</strong>
            </div>
        </div>
    </div>
);

const PublicMinisite = () => {
    const { t } = useI18n();
    const { slug } = useParams();
    const [minisite, setMinisite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false);
    const [selectedPage, setSelectedPage] = useState(0);

    const [formData, setFormData] = useState({});
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSuccess, setFormSuccess] = useState('');

    useEffect(() => {
        if (!slug) return;

        const fetchMinisite = async () => {
            try {
                setLoading(true);
                const response = await minisiteService.getPublic(slug);

                if (!response.status) {
                    setUnavailable(true);
                    return;
                }

                const data = response.data;
                setMinisite(data);

                const pages = (data.pages || []).filter(p => p.status);
                const params = new URLSearchParams(window.location.search);
                const pageSlug = params.get('page');
                if (pageSlug) {
                    const index = pages.findIndex(p => p.slug === pageSlug);
                    if (index !== -1) {
                        setSelectedPage(index);
                        return;
                    }
                }
                const homeIndex = pages.findIndex(p => p.is_home);
                setSelectedPage(homeIndex >= 0 ? homeIndex : 0);
            } catch {
                setUnavailable(true);
            } finally {
                setLoading(false);
            }
        };

        fetchMinisite();
    }, [slug]);

    if (loading) {
        return (
            <div className="pm-loading">
                <div className="pm-spinner" />
            </div>
        );
    }

    if (unavailable || !minisite) return <NotAvailablePage t={t} />;

    const addSectionPage = (minisite.pages || []).find(p => p.status && p.slug === 'add-section');
    const activePages = (minisite.pages || []).filter(p => p.status && p.slug !== 'add-section');
    const currentPage = activePages[selectedPage];

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        setFormSuccess('');
        try {
            await minisiteService.submitForm(slug, formData);
            setFormSuccess(t('messages.public_minisite.submission_success'));
            setFormData({});
        } catch {
            alert(t('errors.form_submit_failed'));
        } finally {
            setFormSubmitting(false);
        }
    };

    const renderAddSection = () => {
        if (!addSectionPage || !addSectionPage.content) return null;

        const hasExplicitHome = activePages.some(p => p.is_home);
        const isCurrentHome = currentPage ? (currentPage.is_home || (!hasExplicitHome && selectedPage === 0)) : true;
        if (activePages.length > 0 && currentPage && !isCurrentHome) return null;

        try {
            const formConfig = JSON.parse(addSectionPage.content);
            if (!formConfig || typeof formConfig !== 'object' || Array.isArray(formConfig)) return null;
            if (!formConfig.fields || formConfig.fields.length === 0) return null;

            return (
                <div className="pm-add-sections">
                    {formConfig.title && <h2 className="pm-form-title">{formConfig.title}</h2>}

                    {formSuccess ? (
                        <div style={{ background: '#dcfce7', color: '#166534', padding: '16px', borderRadius: '8px', fontWeight: 500 }}>
                            {formSuccess}
                        </div>
                    ) : (
                        <form onSubmit={handleFormSubmit} className="pm-form-grid">
                            {formConfig.fields.map((f) => {
                                const id = f.id;
                                const isRequired = f.required || false;
                                const val = formData[f.label] || '';

                                const isHalfWidth = f.type === 'text' || f.type === 'email' || f.type === 'date';
                                const fieldClass = isHalfWidth ? 'pm-form-field-half' : 'pm-form-field-full';

                                let placeholder = '';
                                if (f.label.toLowerCase().includes('name')) placeholder = t('forms.placeholders.example_name');
                                else if (f.label.toLowerCase().includes('email')) placeholder = t('forms.placeholders.example_email');
                                else if (f.label.toLowerCase().includes('message')) placeholder = t('forms.placeholders.example_message');

                                const handleInput = (newVal) => {
                                    setFormData(prev => ({ ...prev, [f.label]: newVal }));
                                };

                                let inputElement = null;

                                if (f.type === 'text' || f.type === 'email' || f.type === 'date') {
                                    inputElement = (
                                        <input
                                            type={f.type}
                                            required={isRequired}
                                            className="pm-form-input"
                                            placeholder={placeholder}
                                            onChange={(e) => handleInput(e.target.value)}
                                            value={val}
                                        />
                                    );
                                } else if (f.type === 'textarea') {
                                    inputElement = (
                                        <textarea
                                            required={isRequired}
                                            rows={4}
                                            className="pm-form-textarea"
                                            placeholder={placeholder}
                                            onChange={(e) => handleInput(e.target.value)}
                                            value={val}
                                        />
                                    );
                                } else if (f.type === 'select') {
                                    const opts = (f.options || '').split(',').map(s => s.trim()).filter(Boolean);
                                    inputElement = (
                                        <select
                                            required={isRequired}
                                            className="pm-form-select"
                                            onChange={(e) => handleInput(e.target.value)}
                                            value={val}
                                        >
                                            <option value="">{t('forms.placeholders.select_default')}</option>
                                            {opts.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                                        </select>
                                    );
                                } else if (f.type === 'radio' || f.type === 'checkbox') {
                                    const opts = (f.options || '').split(',').map(s => s.trim()).filter(Boolean);
                                    inputElement = (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {opts.map((opt, idx) => {
                                                const isChecked = f.type === 'checkbox'
                                                    ? (Array.isArray(val) && val.includes(opt))
                                                    : val === opt;

                                                const toggleCheck = (checked) => {
                                                    if (f.type === 'radio') {
                                                        handleInput(opt);
                                                    } else {
                                                        const currentArr = Array.isArray(val) ? val : [];
                                                        if (checked) handleInput([...currentArr, opt]);
                                                        else handleInput(currentArr.filter(x => x !== opt));
                                                    }
                                                };

                                                return (
                                                    <label className="ui-font-14" key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", color: '#242424' }}>
                                                        <input
                                                            type={f.type}
                                                            name={id}
                                                            checked={isChecked}
                                                            required={f.type === 'radio' ? isRequired : false}
                                                            onChange={(e) => toggleCheck(e.target.checked)}
                                                        />
                                                        {opt}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={id} className={fieldClass}>
                                        <label className="pm-form-label">
                                            {f.label} {isRequired && <span style={{ color: '#dc2626' }}>*</span>}
                                        </label>
                                        {inputElement}
                                    </div>
                                );
                            })}

                            <div className="pm-form-field-full" style={{ marginTop: '10px' }}>
                                <button
                                    type="submit"
                                    disabled={formSubmitting}
                                    className="pm-form-submit"
                                >
                                    {formSubmitting ? t('common.status.submitting') : t('pages.public_minisite.form_submit')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            );
        } catch {
            return null;
        }
    };

    const renderPageContent = (page) => {
        if (!page.content) {
            return <div className="pm-empty-page"><p>{t('pages.public_minisite.empty_page')}</p></div>;
        }

        if (page.slug === 'about-us') {
            return <div className="pm-rich-content" dangerouslySetInnerHTML={{ __html: page.content }} />;
        }

        if (page.slug === 'team') {
            try {
                const teamData = JSON.parse(page.content);
                return (
                    <div className="pm-team">
                        {teamData.team && <p className="pm-team-label">{teamData.team}</p>}
                        <div className="pm-team-grid">
                            {(teamData.members || []).map((member, i) => (
                                <div key={i} className="pm-member-card">
                                    <div className="pm-member-avatar">
                                        {member.image
                                            ? <img src={getStorageUrl(member.image)} alt={member.name} />
                                            : <span>{member.name?.charAt(0)?.toUpperCase() || '?'}</span>
                                        }
                                    </div>
                                    <div className="pm-member-overlay"></div>
                                    <div className="pm-member-info">
                                        <div className="pm-member-name">{member.name}</div>
                                        {member.role && <div className="pm-member-role">{member.role}</div>}
                                        {member.email && <div className="pm-member-email">{member.email}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            } catch {
                return <div className="pm-empty-page"><p>{t('errors.team_content_load_failed')}</p></div>;
            }
        }

        if (page.slug === 'what-we-do') {
            try {
                const services = JSON.parse(page.content);
                if (!Array.isArray(services) || services.length === 0) {
                    return <div className="pm-empty-page"><p>{t('messages.public_minisite.no_services')}</p></div>;
                }
                return (
                    <div className="pm-services">
                        {services.map((service, i) => (
                            <div key={service.id || i} className="pm-service-card">
                                <div className="pm-service-body">
                                    <div className="pm-service-title">{service.title}</div>
                                    {service.description && (
                                        <div className="pm-service-desc">{service.description}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            } catch {
                return <div className="pm-empty-page"><p>{t('errors.services_load_failed')}</p></div>;
            }
        }

        if (page.slug === 'documents') {
            try {
                const docs = JSON.parse(page.content);
                if (!Array.isArray(docs) || docs.length === 0) {
                    return <div className="pm-empty-page"><p>{t('messages.public_minisite.no_documents')}</p></div>;
                }
                return (
                    <div className="pm-services">
                        {docs.map((doc, i) => {
                            const fileUrl = getStorageUrl(doc.file_url);
                            const ext = doc.file_url?.split('.').pop()?.toUpperCase() || t('pages.public_minisite.file_extension_fallback');
                            return (
                                <a
                                    key={doc.id || i}
                                    href={fileUrl}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="pm-service-card"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <div className="pm-service-body">
                                        <div className="pm-service-title">
                                            {doc.description || t('messages.public_minisite.document_title', { number: i + 1 })}
                                        </div>
                                        <div className="pm-service-desc">{t('messages.public_minisite.file_type', { ext })}</div>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                );
            } catch {
                return <div className="pm-empty-page"><p>{t('errors.documents_load_failed')}</p></div>;
            }
        }

        try {
            const blocks = typeof page.content === 'string' ? JSON.parse(page.content) : page.content;
            if (Array.isArray(blocks) && blocks.length > 0) {
                return <BlockRenderer blocks={blocks} />;
            }
        } catch { }

        return <div className="pm-empty-page"><p>{t('pages.public_minisite.empty_page')}</p></div>;
    };

    return (
        <div className="pm-root">
            <header className="pm-header">
                <div className="pm-header-inner">
                    <div className="pm-header-brand">
                        {minisite.logo_url
                            ? <img src={minisite.logo_url} alt="" className="pm-logo" />
                            : <div className="pm-logo-placeholder" />
                        }
                        <h1 className="pm-title">{minisite.title}</h1>
                    </div>
                    {activePages.length > 1 && (
                        <nav className="pm-nav">
                            {activePages.map((page, index) => (
                                <button
                                    key={page.id}
                                    className={`pm-nav-btn${selectedPage === index ? ' active' : ''}`}
                                    onClick={() => setSelectedPage(index)}
                                >
                                    {page.title}
                                </button>
                            ))}
                        </nav>
                    )}
                </div>
            </header>

            <main className="pm-main">
                <div className="pm-container">
                    {currentPage && (
                        <div className="pm-page">
                            <h2 className="pm-page-title">{currentPage.title}</h2>
                            <div className="pm-page-body">
                                {renderPageContent(currentPage)}
                                {renderAddSection()}
                            </div>
                        </div>
                    )}

                    {activePages.length === 0 && (
                        <div className="pm-page">
                            <div className="pm-empty-page">
                                <p>{t('messages.public_minisite.no_published_pages')}</p>
                                {renderAddSection()}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="pm-footer" style={{ marginTop: 'auto' }}>
                <div className="pm-footer-content">
                    {(minisite.contact_email || t('pages.public_minisite.default_contact_email')) && (
                        <div className="pm-footer-item">
                            <img src={MailIcon} alt={t('pages.public_minisite.contact_email_alt')} className="pm-footer-icon" />
                            <span>{minisite.contact_email || t('pages.public_minisite.default_contact_email')}</span>
                        </div>
                    )}
                    {(minisite.contact_phone || t('pages.public_minisite.default_contact_phone')) && (
                        <div className="pm-footer-item">
                            <img src={PhoneIcon} alt={t('pages.public_minisite.contact_phone_alt')} className="pm-footer-icon" />
                            <span>{minisite.contact_phone || t('pages.public_minisite.default_contact_phone')}</span>
                        </div>
                    )}
                    {(minisite.contact_address || t('pages.public_minisite.default_contact_address')) && (
                        <div className="pm-footer-item">
                            <img src={PinIcon} alt={t('pages.public_minisite.contact_address_alt')} className="pm-footer-icon" />
                            <span>{minisite.contact_address || t('pages.public_minisite.default_contact_address')}</span>
                        </div>
                    )}
                </div>
            </footer>
        </div>
    );
};

export default PublicMinisite;
